// The condition kinds the builder offers (spec/dashboard.md section 7.1; spec/liveops-engine.md section 5.1): one entry per
// SmartObjects.Conditions.* template with its group, its label, how it holds children, and the defaults of a new node.
import type { Json } from '../../api/types';

export const CONDITION_NS = 'SmartObjects.Conditions.';
export const LOGIC_TEMPLATE = `${CONDITION_NS}Logic`;
export const BASE_TEMPLATE = `${CONDITION_NS}Base`;

export type ConditionGroup = 'Logic' | 'Inventory' | 'LiveOps' | 'System' | 'Time' | 'Profile' | 'Pass' | 'Custom';

export interface ConditionKind {
  /** the template's last segment: And, ProfileFieldNumber, … */
  key: string;
  template: string;
  group: ConditionGroup;
  label: string;
  /** how the node holds children: a list (`conditions`), one (`condition`), or none */
  children?: 'list' | 'one';
  defaults: Record<string, Json>;
  /** the profile-field leaves carry a fullPath and are picked through the profile tree, not the type list */
  profile?: boolean;
}

const k = (key: string, group: ConditionGroup, label: string, defaults: Record<string, Json> = {}, extra: Partial<ConditionKind> = {}): ConditionKind => ({ key, template: CONDITION_NS + key, group, label, defaults, ...extra });

/** The offered kinds, in the picker's group order (research/06 section 3.1). `DatesRage` is deprecated and never offered. */
export const CONDITION_KINDS: ConditionKind[] = [
  k('InventoryItems', 'Inventory', 'Items', { item: null, comparison: 2, value: 1 }),
  k('ABTestCondition', 'LiveOps', 'A/B test', { test: null, variant: null }),
  k('SegmentCondition', 'LiveOps', 'In segment', { segment: null }),
  k('ActiveEvent', 'LiveOps', 'Is game event active', { gameEvent: null }),
  k('ActiveOffer', 'LiveOps', 'Is game offer active', { gameOffer: null }),
  k('Revenue', 'LiveOps', 'Revenue', { type: 0, value: 1, comparison: 2, days: 7 }),
  k('StoreItemWasPurchased', 'LiveOps', 'Was item purchased', { storeItem: null, comparison: 1, value: 0 }),
  k('ProductWasPurchased', 'LiveOps', 'Was product purchased', { product: null }),
  k('And', 'Logic', 'And', { conditions: [] }, { children: 'list' }),
  k('Not', 'Logic', 'Not', { condition: null }, { children: 'one' }),
  k('Or', 'Logic', 'Or', { conditions: [] }, { children: 'list' }),
  k('AppVersion', 'System', 'App version', { comparison: 2, value: '1.0.0' }),
  k('BalancyPlatform', 'System', 'Balancy platform', { value: 11 }),
  k('Country', 'System', 'Country', { code: '' }),
  k('EngineVersion', 'System', 'Engine version', { comparison: 2, value: '' }),
  k('InstallVersion', 'System', 'Install version', { comparison: 2, value: '1.0.0' }),
  k('SystemLanguage', 'System', 'Language', { value: null }),
  k('Platform', 'System', 'Platform', { value: 11 }),
  k('TimeRage', 'Time', 'Dates range', { start: 0, finish: 0, repeat: 0 }),
  k('DayOfTheWeek', 'Time', 'Day of the week', { days: 65 }),
  k('TimeOfTheDay', 'Time', 'Time of the day', { start: '00:00', finish: '23:00' }),
  k('ProfileFieldNumber', 'Profile', 'Number field', { fullPath: null, comparison: 2, value: 0 }, { profile: true }),
  k('ProfileFieldLong', 'Profile', 'Long field', { fullPath: null, comparison: 2, value: '0' }, { profile: true }),
  k('ProfileFieldString', 'Profile', 'String field', { fullPath: null, comparison: 0, value: '' }, { profile: true }),
  k('ProfileFieldBool', 'Profile', 'Boolean field', { fullPath: null, value: true }, { profile: true }),
  k('ProfileFieldInRange', 'Profile', 'Number in range', { fullPath: null, min: 0, max: 1 }, { profile: true }),
  k('ProfileFieldList', 'Profile', 'List field', { fullPath: null, filter: null, checkType: 0, comparison: 1, value: 0, condition: null }, { profile: true }),
  k('Empty', 'Pass', 'Pass', { pass: true }),
];

export const KIND_BY_TEMPLATE = new Map(CONDITION_KINDS.map((c) => [c.template, c]));

export function kindOf(template: string): ConditionKind | undefined {
  return KIND_BY_TEMPLATE.get(template);
}

export function isConditionTemplate(name: string | null | undefined): boolean {
  return !!name && name.startsWith(CONDITION_NS);
}

/** The template a parameter of the owner names for its tree: `Logic` (And / Or roots) or `Base` (any node, inside Not and filters). */
export function isConditionTarget(template: string | null | undefined): boolean {
  return template === LOGIC_TEMPLATE || template === BASE_TEMPLATE || isConditionTemplate(template);
}

export const COMPARISONS: { value: number; label: string; text: string }[] = [
  { value: 0, label: '==', text: '=' },
  { value: 1, label: '>', text: '>' },
  { value: 2, label: '>=', text: '≥' },
  { value: 3, label: '<', text: '<' },
  { value: 4, label: '<=', text: '≤' },
  { value: 5, label: '!=', text: '≠' },
  { value: 6, label: 'is empty', text: 'is empty' },
];

export const DAYS: { bit: number; name: string }[] = [
  { bit: 1, name: 'Sunday' },
  { bit: 2, name: 'Monday' },
  { bit: 4, name: 'Tuesday' },
  { bit: 8, name: 'Wednesday' },
  { bit: 16, name: 'Thursday' },
  { bit: 32, name: 'Friday' },
  { bit: 64, name: 'Saturday' },
];

/** The day mask in words, Monday first (Q-G-28): 65 → "Saturday, Sunday"; 127 → "every day"; 0 → "no day". */
export function daysText(mask: number): string {
  const order = [2, 4, 8, 16, 32, 64, 1];
  const names = order.filter((bit) => (mask & bit) === bit).map((bit) => DAYS.find((d) => d.bit === bit)!.name);
  if (names.length === 7) return 'every day';
  if (names.length === 0) return 'no day';
  return names.join(', ');
}

export const REVENUE_KINDS: { value: number; label: string }[] = [
  { value: 0, label: 'In-app revenue' },
  { value: 1, label: 'In-app purchases (count)' },
  { value: 2, label: 'Ads revenue (all)' },
  { value: 3, label: 'Ads watched (all)' },
  { value: 4, label: 'Rewarded ads revenue' },
  { value: 5, label: 'Rewarded ads watched' },
  { value: 6, label: 'Interstitial ads revenue' },
  { value: 7, label: 'Interstitial ads watched' },
  { value: 8, label: 'Custom ads revenue' },
  { value: 9, label: 'Custom ads watched' },
];

export const CHECK_TYPES: { value: number; label: string }[] = [
  { value: 0, label: 'Count' },
  { value: 1, label: 'Any' },
  { value: 2, label: 'Each' },
];

export function comparisonText(value: unknown): string {
  return COMPARISONS.find((c) => c.value === Number(value))?.text ?? '?';
}

/** `HH:MM` → minutes of the day; 1440 for "24:00"; null when unparsable. */
export function minutesOf(text: unknown): number | null {
  if (typeof text !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 24 || mi > 59 || (h === 24 && mi > 0)) return null;
  return h * 60 + mi;
}
